import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../../services/api";
import { Container, Row, Col, Badge, Button, Spinner, Card, Modal } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FaCheckCircle, FaCode, FaArrowLeft, FaPlayCircle, FaExternalLinkAlt, FaShoppingCart, FaDownload, FaGithub, FaFileWord, FaFilePdf } from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import ProjectService from "../../services/projectService";
import ProjectCheckoutModal from "../../components/public/ProjectCheckoutModal";
import { toast } from "react-toastify";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  
  // Combine thumbnail and images into one array for the slider
  const allImages = project ? [
    ...(project.thumbnail_url ? [project.thumbnail_url] : []),
    ...(project.images || [])
  ] : [];

  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);
  const slider1 = useRef(null);
  const slider2 = useRef(null);

  useEffect(() => {
    setNav1(slider1.current);
    setNav2(slider2.current);
  }, [project]);

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const res = await ProjectService.getProjectById(id);
      setProject(res.data.data);
    } catch (error) {
      toast.error("Project not found");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <PublicNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh', marginTop: '80px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </>
    );
  }

  if (!project) return null;

  return (
    <>
      <PublicNavbar />
      
      <div className="bg-light" style={{ marginTop: '76px' }}>
        <Container className="py-5">
          <Button variant="link" className="text-decoration-none text-muted mb-3 p-0" onClick={() => navigate('/projects')}>
            <FaArrowLeft className="me-2" /> Back to Projects
          </Button>
          
          <Row>
            {/* Left Column: Media & Description */}
            <Col lg={8} className="mb-4">
              <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                  {/* Slider or Video/Placeholder */}
                  <div className="bg-dark text-white d-flex flex-column" style={{ minHeight: '400px', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', overflow: 'hidden' }}>
                    {allImages.length > 0 ? (
                      <>
                        <div className="w-100" style={{ height: '400px' }}>
                          <Slider 
                            asNavFor={nav2}
                            ref={slider1}
                            arrows={false}
                            infinite={true} 
                            speed={500} 
                            slidesToShow={1} 
                            slidesToScroll={1}
                            autoplay={true}
                            autoplaySpeed={3000}
                          >
                            {allImages.map((img, idx) => (
                              <div key={idx} style={{ height: '400px', outline: 'none', cursor: 'pointer' }} onClick={() => setLightboxImage(img)}>
                                <img 
                                  src={img} 
                                  alt={`${project.title} screenshot ${idx + 1}`} 
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#000' }} 
                                />
                              </div>
                            ))}
                          </Slider>
                        </div>
                        {allImages.length > 1 && (
                          <div style={{ backgroundColor: '#111', padding: '10px 30px' }}>
                            <Slider
                              asNavFor={nav1}
                              ref={slider2}
                              slidesToShow={Math.min(allImages.length, 6)}
                              swipeToSlide={true}
                              focusOnSelect={true}
                              infinite={allImages.length > 6}
                              centerMode={false}
                              arrows={true}
                            >
                              {allImages.map((img, idx) => (
                                <div key={`thumb-${idx}`} className="px-1" style={{ outline: 'none', cursor: 'pointer' }}>
                                  <div style={{ height: '60px', borderRadius: '4px', overflow: 'hidden', border: '2px solid transparent' }} className="thumbnail-wrapper">
                                    <img src={img} alt="thumb" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  </div>
                                </div>
                              ))}
                            </Slider>
                          </div>
                        )}
                      </>
                    ) : project.video_url ? (
                      <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center p-4" style={{ height: '400px' }}>
                        <FaPlayCircle size={60} className="mb-3 text-primary opacity-75" />
                        <h5>Demo Video Available</h5>
                        <Button variant="outline-light" href={project.video_url} target="_blank">Watch on YouTube</Button>
                      </div>
                    ) : (
                       <div style={{ height: '400px' }} className="d-flex align-items-center justify-content-center">
                         <FaCode size={80} className="opacity-25" />
                       </div>
                    )}
                  </div>
                  
                  <div className="p-4 p-md-5">
                    <h2 className="fw-bold mb-3">{project.title}</h2>
                    <div className="mb-4">
                      {project.categories && project.categories.map((c, i) => (
                        <Badge bg="primary" className="me-2 mb-2" key={`c-${i}`}>{c}</Badge>
                      ))}
                      {project.languages && project.languages.map((l, i) => (
                        <Badge bg="info" text="dark" className="me-2 mb-2" key={`l-${i}`}>{l}</Badge>
                      ))}
                    </div>
                    
                    <h5 className="fw-bold border-bottom pb-2 mb-3">Project Description</h5>
                    <div 
                      className="text-muted project-description-html" 
                      style={{ lineHeight: '1.8' }}
                      dangerouslySetInnerHTML={{ __html: project.description }}
                    />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            {/* Right Column: Pricing & Action */}
            <Col lg={4}>
              <Card className="border-0 shadow-sm sticky-top" style={{ top: '100px' }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-4 border-bottom pb-4">
                    <p className="text-muted mb-1">Buy Full Source Code</p>
                    <h1 className="fw-bold text-success mb-0">{parseFloat(project.price) === 0 ? 'FREE' : `₹${project.price}`}</h1>
                  </div>
                  
                  <ul className="list-unstyled mb-4 text-muted">
                    <li className="mb-3"><FaCheckCircle className="text-primary me-2" /> Complete Source Code (ZIP)</li>
                    <li className="mb-3"><FaCheckCircle className="text-primary me-2" /> Database SQL Files</li>
                    <li className="mb-3"><FaCheckCircle className="text-primary me-2" /> Setup Instructions</li>
                    {project.docx_file_name && <li className="mb-3"><FaCheckCircle className="text-primary me-2" /> Project Report (DOCX format)</li>}
                    {project.pdf_file_name && <li className="mb-3"><FaCheckCircle className="text-primary me-2" /> Project Report (PDF format)</li>}
                    {parseFloat(project.price) > 0 && <li className="mb-3"><FaCheckCircle className="text-primary me-2" /> Direct Email Delivery</li>}
                  </ul>

                  {(project.docx_file_name || project.pdf_file_name) && parseFloat(project.price) > 0 && (
                    <div className="bg-light p-3 rounded mb-4 border-start border-primary border-4 shadow-sm">
                      <h6 className="fw-bold mb-2 text-dark">Included Premium Reports:</h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {project.docx_file_name && <Badge bg="primary" className="p-2 px-3"><FaFileWord className="me-1" /> DOCX Report</Badge>}
                        {project.pdf_file_name && <Badge bg="danger" className="p-2 px-3"><FaFilePdf className="me-1" /> PDF Report</Badge>}
                      </div>
                    </div>
                  )}
                  
                  {parseFloat(project.price) === 0 ? (
                    <>
                      <Button 
                        variant="success" 
                        size="lg" 
                        className="w-100 fw-bold shadow-sm d-flex justify-content-center align-items-center mb-3"
                        href={`${API_BASE_URL}/projects/download-free/${project.id}`}
                        target="_blank"
                      >
                        <FaDownload className="me-2" /> Download Source Code (ZIP)
                      </Button>
                      {project.github_url && (
                        <Button 
                          variant="dark" 
                          className="w-100 mb-3 d-flex justify-content-center align-items-center"
                          href={project.github_url}
                          target="_blank"
                        >
                          <FaGithub className="me-2" /> View on GitHub
                        </Button>
                      )}
                    </>
                  ) : (
                    <Button 
                      variant="primary" 
                      size="lg" 
                      className="w-100 fw-bold shadow-sm d-flex justify-content-center align-items-center mb-3"
                      onClick={() => setShowCheckout(true)}
                    >
                      <FaShoppingCart className="me-2" /> Buy Now
                    </Button>
                  )}

                  {project.docx_file_name && parseFloat(project.price) === 0 && (
                    <Button 
                      variant="outline-primary" 
                      className="w-100 mt-3 d-flex justify-content-center align-items-center"
                      href={`${API_BASE_URL}/projects/download-docx/${project.id}`}
                      target="_blank"
                    >
                      <FaFileWord className="me-2" /> Download Project Report (DOCX)
                    </Button>
                  )}

                  {project.pdf_file_name && parseFloat(project.price) === 0 && (
                    <Button 
                      variant="outline-danger" 
                      className="w-100 mt-3 d-flex justify-content-center align-items-center"
                      href={`${API_BASE_URL}/projects/download-pdf/${project.id}`}
                      target="_blank"
                    >
                      <FaFilePdf className="me-2" /> Download Project Report (PDF)
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline-secondary" 
                    className="w-100 mt-3 d-flex justify-content-center align-items-center"
                    href={project.demo_url || "#"}
                    target={project.demo_url ? "_blank" : "_self"}
                    disabled={!project.demo_url}
                  >
                    <FaExternalLinkAlt className="me-2" /> {project.demo_url ? "Live Demo" : "No Demo Available"}
                  </Button>
                  
                  <Button 
                    variant="outline-danger" 
                    className="w-100 mt-3 d-flex justify-content-center align-items-center"
                    href={project.video_url || "#"}
                    target={project.video_url ? "_blank" : "_self"}
                    disabled={!project.video_url}
                  >
                    <FaPlayCircle className="me-2" /> {project.video_url ? "Watch YouTube Demo" : "No Video Available"}
                  </Button>
                  
                  <div className="mt-4 text-center">
                    <small className="text-muted">
                      100% Secure manual UPI payment. Files are delivered automatically via email upon admin verification.
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ProjectCheckoutModal 
        show={showCheckout} 
        onHide={() => setShowCheckout(false)} 
        project={project} 
      />

      {/* Lightbox Modal */}
      <Modal show={!!lightboxImage} onHide={() => setLightboxImage(null)} size="xl" centered>
        <Modal.Header closeButton className="border-0 bg-dark text-white" data-bs-theme="dark"></Modal.Header>
        <Modal.Body className="p-0 bg-dark text-center d-flex align-items-center justify-content-center" style={{ minHeight: '500px' }}>
          {lightboxImage && (
            <img src={lightboxImage} alt="Full screen" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }} />
          )}
        </Modal.Body>
      </Modal>

      <Footer />
      
      {/* Custom CSS for Slider Thumbnails */}
      <style>{`
        .slick-slide.slick-current .thumbnail-wrapper {
          border-color: #0d6efd !important;
          opacity: 1 !important;
        }
        .thumbnail-wrapper {
          opacity: 0.6;
          transition: all 0.2s;
        }
        .thumbnail-wrapper:hover {
          opacity: 1;
        }
        /* Make slick arrows visible on dark background */
        .slick-prev:before, .slick-next:before {
          color: white !important;
        }
      `}</style>
    </>
  );
};

export default ProjectDetails;
